// ── CONNEXION (V2 · sécurité) ────────────────────────────────────────────────
// De vrais comptes Supabase Auth, vérifiés par Supabase et par les règles de la base :
//   - l'équipe se connecte avec son adresse mail et son mot de passe ;
//   - un client garde son identifiant et son mot de passe habituels : la base retrouve le compte
//     qui correspond à l'identifiant, et Supabase vérifie le mot de passe (et limite les essais).
// Aucun mot de passe n'est plus écrit dans le code ni téléchargé dans la page. La session reste
// dans ce navigateur, et son jeton d'accès se renouvelle tout seul.

const STORE = "smrtvzn.session";
let cfg = { url: "", anonKey: "" };
let session = null;
let loaded = false;
let refreshing = null;

export function configureAuth({ url, anonKey }) {
  cfg = { url, anonKey };
}

function load() {
  loaded = true;
  try {
    session = JSON.parse(localStorage.getItem(STORE) || "null");
  } catch {
    session = null;
  }
}

function save(next) {
  session = next;
  loaded = true;
  try {
    if (next) localStorage.setItem(STORE, JSON.stringify(next));
    else localStorage.removeItem(STORE);
  } catch {
    /* navigation privée : la session vit le temps de l'onglet */
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === STORE) load();
  });
}

const failure = (message, status) => Object.assign(new Error(message), { status });

async function token(grant, body) {
  const res = await fetch(`${cfg.url}/auth/v1/token?grant_type=${grant}`, {
    method: "POST",
    headers: { apikey: cfg.anonKey, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 429) throw failure("Trop d'essais. Réessaie dans quelques minutes.", 429);
    throw failure(data.error_description || data.msg || data.message || "Connexion refusée", res.status);
  }
  return {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    expires_at: data.expires_at || Math.floor(Date.now() / 1000) + (data.expires_in || 3600),
    // temp : mot de passe provisoire donné par un admin, à remplacer à la première connexion.
    user: { id: data.user?.id, email: data.user?.email, temp: !!data.user?.user_metadata?.temp_password },
  };
}

/** Connexion par adresse et mot de passe (l'équipe, ou un client une fois son compte retrouvé). */
export async function signIn(email, password) {
  const next = await token("password", { email: String(email || "").trim().toLowerCase(), password });
  save(next);
  return next;
}

/**
 * Le mot de passe d'un client tel que Supabase Auth le garde : insensible à la casse et aux espaces
 * comme avant, et toujours assez long pour Supabase. Même règle côté serveur (api/_acces-regles.js).
 */
export const clientSecret = (password) => `smrtvzn:${String(password || "").trim().toLowerCase()}`;

/** Connexion d'un client avec son identifiant habituel (label, artiste ou campagne). */
export async function signInClient(typedId, password) {
  const res = await fetch(`${cfg.url}/rest/v1/rpc/login_candidates`, {
    method: "POST",
    headers: { apikey: cfg.anonKey, Authorization: `Bearer ${cfg.anonKey}`, "Content-Type": "application/json" },
    body: JSON.stringify({ typed: typedId }),
  });
  if (!res.ok) throw failure("Connexion impossible pour le moment. Réessaie plus tard.", res.status);
  const rows = await res.json();
  const emails = (Array.isArray(rows) ? rows : []).map((r) => (typeof r === "string" ? r : r && r.login_candidates)).filter(Boolean);
  for (const email of emails) {
    try {
      return await signIn(email, clientSecret(password));
    } catch (error) {
      if (error.status === 429) throw error;
    }
  }
  throw failure("Identifiant ou mot de passe incorrect", 400);
}

export async function signOut() {
  const previous = session;
  save(null);
  if (previous?.access_token) {
    fetch(`${cfg.url}/auth/v1/logout`, {
      method: "POST",
      headers: { apikey: cfg.anonKey, Authorization: `Bearer ${previous.access_token}` },
    }).catch(() => {});
  }
}

/** Vrai si la personne connectée a encore le mot de passe provisoire donné par un admin. */
export function mustChangePassword() {
  return !!currentSession()?.user?.temp;
}

/** La personne connectée choisit son mot de passe (Supabase Auth le garde chiffré). */
export async function changePassword(password) {
  const access = await accessToken();
  if (!access) throw failure("Session expirée : reconnecte-toi.", 401);
  const res = await fetch(`${cfg.url}/auth/v1/user`, {
    method: "PUT",
    headers: { apikey: cfg.anonKey, Authorization: `Bearer ${access}`, "Content-Type": "application/json" },
    body: JSON.stringify({ password, data: { temp_password: false } }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const code = data.error_code || data.code;
    if (code === "same_password") throw failure("Choisis un mot de passe différent de l'actuel.", 422);
    if (code === "weak_password") throw failure("Mot de passe trop faible : choisis-en un plus long.", 422);
    if (code === "reauthentication_needed") throw failure("Reconnecte-toi, puis change ton mot de passe tout de suite.", 401);
    throw failure(data.msg || data.message || "Le mot de passe n'a pas pu être changé.", res.status);
  }
  if (session) save({ ...session, user: { ...session.user, temp: false } });
  return true;
}

export function currentSession() {
  if (!loaded) load();
  return session;
}

/** Un jeton d'accès valable (renouvelé s'il expire dans moins d'une minute), ou null sans session. */
export async function accessToken() {
  if (!loaded) load();
  if (!session) return null;
  if (session.expires_at - Date.now() / 1000 > 60) return session.access_token;
  if (!refreshing) {
    const refreshToken = session.refresh_token;
    refreshing = token("refresh_token", { refresh_token: refreshToken })
      .then((next) => {
        save(next);
        return next.access_token;
      })
      .catch((error) => {
        // Un autre onglet a peut-être déjà renouvelé la session : on relit avant d'abandonner.
        load();
        if (session && session.refresh_token !== refreshToken && session.expires_at - Date.now() / 1000 > 60) return session.access_token;
        if (error.status === 400 || error.status === 401 || error.status === 403) save(null);
        return null;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

/** Les en-têtes d'un appel à la base : le jeton de la personne connectée (la clé publique seule ne donne rien). */
export async function authHeaders(extra = {}) {
  const access = await accessToken();
  return { apikey: cfg.anonKey, Authorization: `Bearer ${access || cfg.anonKey}`, ...extra };
}

/** Qui est connecté : { kind: "staff", role, name } | { kind: "client", login, ref } | { kind: "none" }. */
export async function whoami() {
  try {
    const res = await fetch(`${cfg.url}/rest/v1/rpc/whoami`, {
      method: "POST",
      headers: await authHeaders({ "Content-Type": "application/json" }),
      body: "{}",
    });
    if (!res.ok) return { kind: "none" };
    return await res.json();
  } catch {
    return { kind: "none" };
  }
}
